import type { MessageResponse } from '@/api';

export type MessageData = Record<string, unknown>;

// Same keys, same order as the backend's `utils/extract.rs`.
const EMAIL_KEYS = ['email', 'e-mail', 'mail', 'email_address', 'your_email', 'contact_email'];
const NAME_KEYS = ['name', 'full_name', 'your_name', 'first_name'];
const BODY_KEYS = ['message', 'comment', 'comments', 'text', 'body', 'content', 'description'];

export const SPAM_THRESHOLD = 75;

export function isNostr(message: Pick<MessageResponse, 'encryption_mode'>): boolean {
  return message.encryption_mode === 'nostr';
}

/** Parsed `data`, or null for nostr rows and anything that is not a JSON object. */
export function parseMessageData(message: Pick<MessageResponse, 'data'>): MessageData | null {
  if (!message.data) return null;
  try {
    const parsed: unknown = JSON.parse(message.data);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as MessageData)
      : null;
  } catch {
    return null;
  }
}

export function valueText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(valueText).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function firstByKeys(data: MessageData, keys: string[]): [string, string] | null {
  const entries = Object.entries(data);
  for (const key of keys) {
    const hit = entries.find(([k, v]) => k.toLowerCase() === key && typeof v === 'string');
    if (hit && (hit[1] as string).trim()) return [hit[0], (hit[1] as string).trim()];
  }
  return null;
}

/** Sender from decrypted data, for nostr rows the backend cannot read. */
export function senderFromData(data: MessageData): { name?: string; email?: string } {
  const email = firstByKeys(data, EMAIL_KEYS)?.[1];
  const nameHit = firstByKeys(data, NAME_KEYS);
  let name = nameHit?.[1];
  if (nameHit && nameHit[0].toLowerCase() === 'first_name') {
    const last = firstByKeys(data, ['last_name'])?.[1];
    if (last) name = `${name} ${last}`;
  }
  return { name, email };
}

/** The text a reader wants to see first: the message body, else the longest other value as `key: value`. */
export function firstLine(data: MessageData): string {
  const body = firstByKeys(data, BODY_KEYS);
  if (body) return body[1].split('\n')[0];
  const skip = new Set([...EMAIL_KEYS, ...NAME_KEYS, 'last_name']);
  let best: [string, string] | null = null;
  for (const [key, value] of Object.entries(data)) {
    if (skip.has(key.toLowerCase())) continue;
    const text = valueText(value).trim();
    if (text && (!best || text.length > best[1].length)) best = [key, text];
  }
  return best ? `${best[0]}: ${best[1].split('\n')[0]}` : '';
}

/** 0 to 100, the one scale the UI uses. */
export function spamScore(message: Pick<MessageResponse, 'spam_score'>): number {
  const raw = Number.isFinite(message.spam_score) ? message.spam_score : 0;
  return Math.round(Math.min(Math.max(raw, 0), 1) * 100);
}

export type Verdict =
  | { kind: 'encrypted' }
  | { kind: 'unscored' }
  | { kind: 'spam' | 'notSpam'; score: number; byYou: boolean; scored: boolean };

type VerdictInput = Pick<
  MessageResponse,
  'encryption_mode' | 'is_spam' | 'spam_score' | 'user_marked_spam' | 'signal_hits'
>;

/**
 * What a message's tag and verdict box say. `filterOn` is the form's spam filter;
 * undefined when the form is unknown, which keeps the score visible.
 */
export function verdictOf(message: VerdictInput, filterOn: boolean | undefined): Verdict {
  if (isNostr(message)) return { kind: 'encrypted' };
  const score = spamScore(message);
  // A filter-off form stores score 0 and no hits; a score from before it was switched off still counts.
  const scored = filterOn !== false || score > 0 || signalHits(message).length > 0;
  const kind = message.is_spam ? 'spam' : 'notSpam';
  const scoreSaysSpam = message.spam_score > SPAM_THRESHOLD / 100;
  const byYou = !!message.user_marked_spam && message.is_spam !== (scored && scoreSaysSpam);
  if (!scored && !byYou) return { kind: 'unscored' };
  return { kind, score, byYou, scored };
}

/** The i18n key and values of a verdict's tag text, in `ui`. */
export function verdictLabel(v: Verdict): { key: string; values?: Record<string, number> } {
  switch (v.kind) {
    case 'encrypted':
      return { key: 'ui.verdict.encrypted' };
    case 'unscored':
      return { key: 'ui.verdict.unscored' };
    default:
      if (v.byYou) {
        const base = v.kind === 'spam' ? 'ui.verdict.markedSpam' : 'ui.verdict.markedNotSpam';
        return v.scored ? { key: `${base}Score`, values: { score: v.score } } : { key: base };
      }
      return {
        key: v.kind === 'spam' ? 'ui.verdict.spam' : 'ui.verdict.notSpam',
        values: { score: v.score },
      };
  }
}

export interface SignalHit {
  signal: string;
  strength: number;
}

/** `signal_hits` as stored: `[{ signal, strength }]`, strongest first. Unknown shapes yield nothing. */
export function signalHits(message: Pick<MessageResponse, 'signal_hits'>): SignalHit[] {
  const raw = message.signal_hits;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (h): h is SignalHit =>
        !!h && typeof h === 'object' && typeof (h as SignalHit).signal === 'string'
    )
    .map((h) => ({ signal: h.signal, strength: Number(h.strength) || 0 }))
    .sort((a, b) => b.strength - a.strength);
}

export function downloadText(content: string | Blob, filename: string, type = 'text/plain') {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Firefox still reads the URL after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function messageAsJson(data: MessageData): string {
  return JSON.stringify(data, null, 2);
}

export function messageAsText(data: MessageData): string {
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${valueText(value)}`)
    .join('\n');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
